/**
 * An admin signed in as a member from the admin panel.
 *
 * The session user's `impersonated_by` names the admin while it lasts. The
 * session is read-only, so signing out is a leave (`/logout` is a write, and
 * would be refused), and the way out of every end — leaving, or the 30-minute
 * window closing on a 401 — is the admin panel, where the browser is the admin
 * again. Member routes answer 403 to an admin, so nothing here navigates
 * inside the app.
 */

import { authService } from '@personal-trainer-app/shared';

import type { User } from '@personal-trainer-app/shared';

import { useAuthStore } from '@/stores';

export const IMPERSONATION_COPY = {
  /** The banner's line; the names are drawn bold around it. */
  signedInAs: 'is signed in as',
  fromPanel: 'from the admin panel. Read-only.',
  leave: 'Leave',
  /** Sign Out's row, while it leaves instead. */
  leaveAccount: (username: string): string => `Leave ${username}'s account`,
  leaveAccountSubtitle: 'Back to the admin panel',
} as const;

/** The admin signed in as this member, or null when it is the member. */
export function impersonatorOf(
  user: Pick<User, 'impersonated_by'> | null | undefined
): string | null {
  return user?.impersonated_by ?? null;
}

/** Send the browser to the admin panel, out of the app. */
export function goToAdminPanel(): void {
  window.location.assign(authService.adminPanelUrl());
}

/**
 * Go to the admin panel the moment the session user stops being the member an
 * admin was signed in as — however the app learned it. The window's 401 can
 * reach a request the request layer handles (see `handleAuthError`), but just
 * as well the session re-read on returning to the tab, which goes round it:
 * that read takes a 401 as "no session" and clears the user, which would land
 * on the login page; or, read after the 401, it answers with the ADMIN's
 * account, which would be applied as the user. Either way the member is gone
 * and the browser is the admin, so neither the login page nor the app is
 * right. Returns the unsubscribe.
 */
export function watchImpersonationEnd(): () => void {
  return useAuthStore.subscribe((state, prev) => {
    const admin = impersonatorOf(prev.user);
    if (!admin || !prev.user) return;

    const user = state.user;
    if (
      user &&
      user.username === prev.user.username &&
      impersonatorOf(user) === admin
    ) {
      return;
    }

    goToAdminPanel();
  });
}

/**
 * Leave the member's account and go back to the admin panel.
 *
 * Goes there whatever leave answers: a 204 made the browser the admin, and a
 * refusal (the window closed, or it wasn't impersonating) leaves it the admin
 * already — either way no member page will open for it.
 */
export async function leaveImpersonation(): Promise<void> {
  try {
    await authService.leaveImpersonation();
  } catch {
    // The admin panel is the way out either way.
  }
  goToAdminPanel();
}
